import {
    Fields,
    Genders,
    TopIssues,
    RuralStatus,
    Timezones,
    States,
    integerOperators,
    inOperator,
    onlyInOperator,
    dateOperators,
    stringOperators,
    onlyContainsOperator,
} from './constants';
import { StandardBaskets } from './baskets';
import { FilterPresets } from '@/store/filterPresets';

function isNullOperator(fieldOperator) {
    return fieldOperator === 'IS NULL' || fieldOperator === 'IS NOT NULL';
}

export function iterateThroughQueryFilters(filters, func, func_params) {
    if (filters !== null) {
        for (var [index, child] of filters.entries()) {
            if (child.type === 'group') {
                iterateThroughQueryFilters(child.children, func, func_params);
            } else {
                let param_blob = {
                    filters: filters,
                    index: index,
                    child: child,
                    ...func_params,
                };
                func(param_blob);
            }
        }
    }
}

export function validateSearchType(searchTypeValue) {
    return FilterPresets.find((f) => f.value === searchTypeValue) !== undefined;
}

export function validateQueryFilters(filterList, errorLogger, toastObj) {
    function storeRuleValidation(param_blob) {
        param_blob.filterValidation.push(validateRule(param_blob.child, errorLogger, toastObj));
    }

    if (filterList !== null) {
        let params = { filterValidation: [] };
        iterateThroughQueryFilters(filterList, storeRuleValidation, params);
        return params.filterValidation.every(Boolean);
    }

    errorLogger('Empty filter list.', toastObj);
    return false;
}

export function validateRule(rule, errorLogger, toastObj) {
    let valid = true;
    let matchingField = Fields.find((f) => f.name === rule.field);
    if (matchingField !== undefined) {
        if (!validateBasket(rule, matchingField)) {
            valid = false;
            errorLogger(
                JSON.stringify(rule.basket) + ` is not a valid basket input for field '${rule.field}'.`,
                toastObj
            );
        }

        if (!validateOperator(rule, matchingField.operators)) {
            valid = false;
            errorLogger(`${rule.operator} is not a valid operator for field '${rule.field}'.`, toastObj);
        }

        if (!validateValue(rule, matchingField.component.name)) {
            valid = false;
            errorLogger(JSON.stringify(rule.value) + ` is not a valid value for field '${rule.field}'.`, toastObj);
        }
    } else {
        valid = false;
        errorLogger(`Invalid field: '${rule.field}'.`, toastObj);
    }
    return valid;
}

export function validateOperator(rule, allowedOperators) {
    return allowedOperators.includes(rule.operator);
}

export function validateValue(rule, fieldComponentName) {
    if (isNullOperator(rule.operator)) {
        return true;
    } else if (fieldComponentName === 'FloatRule') {
        return typeof rule.value === 'number' && validateFloats(rule.value);
    } else if (fieldComponentName === 'IntRule') {
        return typeof rule.value === 'number' && validateInts(rule.value);
    } else if (fieldComponentName === 'TextRule') {
        return (
            typeof rule.value === 'string' ||
            (Array.isArray(rule.value) && rule.value.every((f) => typeof f === 'string'))
        );
    } else if (fieldComponentName === 'BoolRule') {
        return rule.value === 0 || rule.value === 1;
    } else if (fieldComponentName === 'DateRule') {
        // Example of a date value: [{ seconds: 1680321600, nanoseconds: 0 }]
        if (Array.isArray(rule.value)) {
            for (var elem of rule.value) {
                return validateDate(elem);
            }
        } else {
            return validateDate(rule.value);
        }
    } else if (fieldComponentName === 'TopIssueRule') {
        return rule.value.every((v) => TopIssues.find((f) => f.key === v.key) !== undefined);
    } else if (fieldComponentName === 'GenderRule') {
        return Genders.find((f) => f.key === rule.value) !== undefined;
    } else if (fieldComponentName === 'RuralRule') {
        return RuralStatus.find((f) => f.key === rule.value) !== undefined;
    } else if (fieldComponentName === 'StateRule') {
        return rule.value.every((v) => States.find((f) => f.key === v.key) !== undefined);
    } else if (fieldComponentName === 'DonationBasketRule' || fieldComponentName === 'DistinctDonationsBasketRule') {
        return typeof rule.value === 'number' && validateInts(rule.value);
    } else if (fieldComponentName === 'DemographicBasketRule') {
        return rule.value === null;
    } else if (fieldComponentName === 'TimezoneRule') {
        return rule.value.every((v) => Timezones.find((f) => f.key === v.key) !== undefined);
    }
    return false;
}

export function validateFloats(ruleValue) {
    const floatRegex = new RegExp('^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$');
    return floatRegex.test(ruleValue);
}

export function validateInts(ruleValue) {
    const intRegex = new RegExp('^[0-9]+$');
    return intRegex.test(ruleValue);
}

export function validateDate(ruleValue) {
    if (typeof ruleValue === 'object') {
        return (
            'seconds' in ruleValue &&
            typeof ruleValue['seconds'] === 'number' &&
            'nanoseconds' in ruleValue &&
            typeof ruleValue['nanoseconds'] === 'number'
        );
    } else if (typeof ruleValue === 'string') {
        const dateRegex = /^\d{4}-[0-1]\d-[0-3]\dT([0-1][0-9]|2[0-3]):[0-5]\d:[0-5]\d.000Z$/;
        return ruleValue.match(dateRegex);
    }
}

export function validateBasket(rule, matchedField) {
    const lowercasedRuleName = rule.field.toLowerCase();
    if (matchedField.component.name === 'DemographicBasketRule') {
        return validateDemographicBasket(rule);
    } else if (matchedField.component.name === 'DonationBasketRule') {
        return validateDonationBasket(rule);
    } else if (lowercasedRuleName.includes('standard basket')) {
        return StandardBaskets.find((f) => f.name === rule.basket[0]) !== undefined;
    } else if (
        matchedField.component.name === 'DistinctDonationsBasketRule' ||
        lowercasedRuleName.includes('custom basket')
    ) {
        return rule.basket.every((i) => typeof i === 'string');
    } else if (matchedField.component.name === 'FloatRule') {
        return validateValue(rule, matchedField.component.name);
    }
    return rule.basket === null;
}

export function validateDemographicBasket(rule) {
    let validProps = {
        first_name: { operator: stringOperators, ruleType: 'TextRule' },
        last_name: { operator: stringOperators, ruleType: 'TextRule' },
        work_history: { operator: stringOperators, ruleType: 'TextRule' },
    };

    for (var key in rule.basket) {
        if (
            key !== 'type' &&
            (!validateOperator(rule.basket[key], validProps[key]['operator']) ||
                !validateValue(rule.basket[key], validProps[key]['ruleType']))
        ) {
            return false;
        }
    }
    return true;
}

export function validateDonationBasket(rule) {
    let validProps = {
        amount: { operator: integerOperators, ruleType: 'IntRule' },
        candidates: { operator: onlyInOperator.concat(onlyContainsOperator), ruleType: 'TextRule' },
        candidate_state: { operator: inOperator, ruleType: 'StateRule' },
        date: { operator: dateOperators, ruleType: 'DateRule' },
        district: { operator: integerOperators, ruleType: 'IntRule' },
    };

    for (var key in rule.basket) {
        if (
            key !== 'type' &&
            (!validateOperator(rule.basket[key], validProps[key]['operator']) ||
                !validateValue(rule.basket[key], validProps[key]['ruleType']))
        ) {
            return false;
        }
    }
    return true;
}
